import React, { FC, useState, useEffect } from 'react';
import moment from 'moment';
import propTypes from 'prop-types';
import ProgressBar from '../ProgressBar';
import { SuccessStatusSvg, ErrorStatusSvg, WarningStatusSvg } from '../../svgs';
import MoreInfoModal from '../MoreInfoModal';
import './style.scss';

interface UploadEventDetails {
  eventName: string;
  uploadTime: string;
  lastUpdated: string;
  status: string;
  warningText?: string;
}

export enum MEDIA_STATUS_ENUM {
  STARTING = 'STARTING',
  IN_PROGRESS = 'IN_PROGRESS',
  FAILED = 'FAILED',
  COMPLETE = 'COMPLETE',
  CANCELLED = 'CANCELLED',
  RETRYING = 'RETRYING',
  TIMED_OUT = 'TIMED_OUT',
}

const EVENT_STATUS_TEXT = {
  STARTING: 'Starting',
  READY: 'Event Ready',
  PROCESSING: 'Progress (Processing)',
  FAILED: 'Processing Failed',
  CANCELLED: 'Upload Cancelled',
  RETRY: 'Retrying Upload',
  TIMED_OUT: 'Failed to Process in a Timely Manner',
};

export interface ImportResponse {
  customerId: string;
  importId: string;
  userId: string;
  name: string;
  created: string;
  updated: string;
  percentProcessed: number;
  status: MEDIA_STATUS_ENUM;
  complete: boolean;
  message?: string;
}

export interface UploadStatusProgressChartProps {
  eventList: ImportResponse[];
  backToEvents: () => void;
}

const UploadStatusProgressChart: FC<UploadStatusProgressChartProps> = ({ backToEvents, eventList }): JSX.Element => {
  const [moreInfoMsg, setMoreInfoMsg] = useState<string | null>(null);
  const [orderedEventList, setOrderedEventList] = useState<ImportResponse[]>([]);

  useEffect(() => {
    setOrderedEventList(eventList.sort((a,b) => new Date(a.created) < new Date(b.created) ? 1 : -1));
  }, [eventList]);

  function eventTextSwitch(eventStatus: MEDIA_STATUS_ENUM, progress: number, statusMsg?: string): JSX.Element {
    switch (eventStatus) {
      case MEDIA_STATUS_ENUM.STARTING:
        return (
            <div data-testid="starting-row" className="uploadsInProgress-status-wrap">
              {EVENT_STATUS_TEXT.STARTING}
            </div>
        )
      case MEDIA_STATUS_ENUM.IN_PROGRESS:
        return (
          <div data-testid="in-progress-row" className="uploadsInProgress-status-wrap">
            <div>
              <div>{EVENT_STATUS_TEXT.PROCESSING}</div>
              <div className="uploadsInProgress-progress-bar-wrap">
                <div className="uploadsInProgress-progress-inner-wrap">
                  <ProgressBar progressUpdate={progress}></ProgressBar>
                </div>
                <div className="uploadsInProgress-progress-txt">
                  ({`${progress || 0}%`})
                </div>
              </div>
            </div>
          </div>
        )
      case MEDIA_STATUS_ENUM.FAILED:
        return (
          <div data-testid="failed-row" className="uploadsInProgress-status-wrap">
            <ErrorStatusSvg />
            {EVENT_STATUS_TEXT.FAILED}
            {statusMsg && statusMsg.length > 0 && <button data-testid="more-info-btn" onClick={() => setMoreInfoMsg(statusMsg)} className="btn btn-default uploadsInProgress-more-info-btn">More Info</button>}
          </div>
        )
      case MEDIA_STATUS_ENUM.COMPLETE:
        return (
          <div data-testid="complete-row" className="uploadsInProgress-status-wrap">
            <SuccessStatusSvg />
            {EVENT_STATUS_TEXT.READY}
          </div>
        )
      case MEDIA_STATUS_ENUM.CANCELLED:
        return (
          <div  data-testid="cancelled-row"className="uploadsInProgress-status-wrap">
            <WarningStatusSvg />
            <div>{EVENT_STATUS_TEXT.CANCELLED}</div>
          </div>
        )
      case MEDIA_STATUS_ENUM.RETRYING:
        return (
          <div data-testid="retrying-row" className="uploadsInProgress-status-wrap">
            <WarningStatusSvg />
            <div>{EVENT_STATUS_TEXT.RETRY}</div>
          </div>
        )
      case MEDIA_STATUS_ENUM.TIMED_OUT:
        return (
          <div data-testid="timed-out-row" className="uploadsInProgress-status-wrap">
            <ErrorStatusSvg />
            <div>{EVENT_STATUS_TEXT.TIMED_OUT}</div>
          </div>
        )
      default:
        return <div>Invalid Event Status</div>;
    }
  }

  function backBtnKeypress(e: any) {
    if (e.keyCode === 27) {
      backToEvents();
    }
  }

  return (
    <>
      <div className="col-md-12">
        <div className="uploadsInProgress-main-header-wrap">
          <i className="fa fa-upload" id="uploadsInProgress-upload-icon-size"></i>
          <div className="uploadsInProgress-header-wrap">
            <h1 className="uploadsInProgress-main-header">Recent Uploads
              <i
                className="fa fa-question-circle fa-icon-muted"
                id="uploadsInProgress-tooltip-icon"
                data-toggle="tooltip"
                data-placement="right"
                title=""
                data-original-title="Recent uploads includes uploads from the past 72 hours."
              />
            </h1>
            <button onClick={backToEvents} onKeyPress={backBtnKeypress} className="btn btn-default">
              <i className="fa fa-mail-reply" style={{ marginRight: 7 }} />
                Back
              </button>
          </div>
        </div>
        <div className="box">
          {orderedEventList.length === 0 && <div className="uploadsInProgress-no-existing-events">There are no recent uploads.</div>}
          <table className="table table-hover align-middle ng-scope">
          {orderedEventList.length > 0 && <tbody>
            <tr>
              <th>Event Name</th>
              <th>Upload Time</th>
              <th>Last Updated</th>
              <th className="uploadsInProgress-status-width">Status</th>
            </tr>
          {orderedEventList && orderedEventList.map((event, i) => {
            return (
              <tr key={`event-progress-${event.importId}`}>
                <td>{event.name}</td>
                <td>{moment(event.created).format('ddd, MMMM D, YYYY h:mm:ss A')}</td>
                <td>{moment(event.updated).format('ddd, MMMM D, YYYY h:mm:ss A')}</td>
                <td>
                  {eventTextSwitch(event.status, +event.percentProcessed, event.message)}
                </td>
              </tr>
            )
          })
          }
          </tbody>}
          </table>
        </div>
      </div>
      {moreInfoMsg !== null && <MoreInfoModal title="Upload Status More Info" closeModal={() => setMoreInfoMsg(null)}>
        <div>{moreInfoMsg}</div>
        <a href="https://resi.io/documentation/file-upload" target="_blank" rel="noopener noreferrer">
          Upload Video Documentation
        </a>
      </MoreInfoModal>}
    </>
  )
}

UploadStatusProgressChart.propTypes = {
  eventList: propTypes.array.isRequired,
  backToEvents: propTypes.func.isRequired
};

UploadStatusProgressChart.displayName = 'UploadStatusProgressChart';

export default UploadStatusProgressChart;
